import { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import { PerformRequest, usePerformRequest } from '../lib/usePerformRequest';
import { NonPaginatedResponse } from '../types';
import { IUserProps } from '../types/User';
import { Endpoints } from '../lib/endpoints';

export interface AuthContextType {
  user: IUserProps | null;
  loading: boolean;
  login: (emailOrPhone: string, password: string) => Promise<void>;
  register: (email: string, phone: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  reloadUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const STORAGE_TOKEN_KEY = 't';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [hasToken, setHasToken] = useState(() => !!localStorage.getItem(STORAGE_TOKEN_KEY));
  
  // Update route based on token state - this triggers usePerformRequest to fetch when route changes from '' to actual route
  const profileRoute = hasToken ? Endpoints.GetProfile().route : '';

  const { 
    data: profileResponse, 
    isLoading: isLoadingUser, 
    reloadData: reloadUserFunc,
    status: profileStatus,
  } = usePerformRequest<NonPaginatedResponse<IUserProps>>({
    method: Endpoints.GetProfile().method,
    route: profileRoute,
  });

  // Extract user from API response - profileResponse is NonPaginatedResponse<IUserProps>
  // so profileResponse.data contains the IUserProps
  const user = profileResponse?.data || null;
  
  // Use ref to track user for polling in reloadUser
  const userRef = useRef<IUserProps | null>(null);
  userRef.current = user;

  // Sync token state with localStorage
  useEffect(() => {
    const checkToken = () => {
      const currentToken = localStorage.getItem(STORAGE_TOKEN_KEY);
      setHasToken(!!currentToken);
    };
    
    // Check on mount
    checkToken();
    
    // Listen for storage changes (when token is set/removed in other tabs/components)
    window.addEventListener('storage', checkToken);
    
    // Check token periodically (for same-tab updates)
    const interval = setInterval(checkToken, 100);
    
    return () => {
      window.removeEventListener('storage', checkToken);
      clearInterval(interval);
    };
  }, []);

  // Handle auth errors - clear token if unauthorized
  useEffect(() => {
    if (profileStatus === 401 || profileStatus === 403) {
      localStorage.removeItem(STORAGE_TOKEN_KEY);
      setHasToken(false);
    }
  }, [profileStatus]);

  // Loading state: loading when:
  // 1. We're actively fetching user data (isLoadingUser)
  // 2. We have a token but no user yet (hasToken && !user) - prevents redirect while loading
  const loading = (isLoadingUser && hasToken) || (hasToken && !user);

  const login = async (emailOrPhone: string, password: string): Promise<void> => {
    // Login is handled in Login component - just a placeholder
    return Promise.resolve();
  };

  const register = async (email: string, phone: string, password: string): Promise<void> => {
    // Registration is handled in Register component - just a placeholder
    return Promise.resolve();
  };

  const logout = async (): Promise<void> => {
    localStorage.removeItem(STORAGE_TOKEN_KEY);
    setHasToken(false);
  };

  const handleReloadUser = async (): Promise<void> => {
    const currentToken = localStorage.getItem(STORAGE_TOKEN_KEY);
    if (currentToken) {
      // Force update hasToken state immediately to trigger profile fetch
      setHasToken(true);
      // Wait for React to process the state update and route to be updated
      // This ensures usePerformRequest sees the route change
      await new Promise(resolve => setTimeout(resolve, 100));
      // Explicitly reload to ensure fresh data - this will fetch the profile
      await reloadUserFunc();
      
      // Poll until user is actually loaded (state update might be async)
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max wait
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 100));
        // Check ref to get latest user value
        if (userRef.current) {
          break;
        }
        attempts++;
      }
    }
  };

  
  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      reloadUser: handleReloadUser 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
